import BatchInvoiceIcon from "@mui/icons-material/Receipt";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab as FloatingActionButton,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { has, isNil, omit, uniq, uniqBy } from "lodash";
import type { FC } from "react";
import { useState } from "react";
import LocalFilePicker from "../components/associatedFiles/LocalFilePicker";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../components/cancelButton/CancelButton";
import DatePicker from "../components/datePicker/DatePicker";
import NumberField from "../components/numberField/NumberField";
import { Select } from "../components/select/Select";
import dayjs, { ISO_DATE_FORMAT } from "../dayjs";
import CostCurrencySelect from "./CostCurrencySelect";
import CostExchangeRate from "./CostExchangeRate";
import type { CostCurrency, CostInvoiceStatus } from "./costApi";
import type { Cost } from "./costTypes";
import InvoiceStatusSelect from "./edit/InvoiceStatusSelect";
import { useCostsInvoice } from "./useCostsInvoice";

interface SelectedExchangeRates {
  EUR?: number | null;
  PLN?: number | null;
}

interface ExchangeRatesProps {
  sourceCurrency: CostCurrency;
  date: string;
  exchangeRates: {
    EUR?: number | null;
    PLN?: number | null;
  };
  onChange: (exchangeRates: SelectedExchangeRates) => void;
}

const ExchangeRates: FC<ExchangeRatesProps> = ({ sourceCurrency, date, exchangeRates, onChange }) => {
  return (
    <Grid container size={12}>
      {has(exchangeRates, "EUR") && (
        <Grid size={6}>
          <CostExchangeRate
            sourceCurrency={sourceCurrency}
            targetCurrency="EUR"
            value={exchangeRates.EUR}
            date={date}
            onChange={(EUR) => onChange({ ...exchangeRates, EUR: EUR ?? null })}
          />
        </Grid>
      )}
      {has(exchangeRates, "PLN") && (
        <Grid size={6}>
          <CostExchangeRate
            sourceCurrency={sourceCurrency}
            targetCurrency="PLN"
            value={exchangeRates.PLN}
            date={date}
            onChange={(PLN) => onChange({ ...exchangeRates, PLN: PLN ?? null })}
          />
        </Grid>
      )}
    </Grid>
  );
};

interface CostListItemProps {
  readonly cost: Cost;
  readonly selected: boolean;
  readonly value: number | undefined;
  readonly currency: CostCurrency;
  readonly onSelectionChange: (selected: boolean) => void;
  readonly onValueChange: (value: number | null | undefined) => void;
}

const CostListItem: FC<CostListItemProps> = ({ cost, selected, value, currency, onSelectionChange, onValueChange }) => {
  const labelId = `checkbox-list-label-${cost.id}`;
  return (
    <ListItem
      disablePadding
      secondaryAction={
        selected ? (
          <NumberField label="Kwota" suffix={currency} size="small" value={value} onChange={onValueChange} />
        ) : undefined
      }
    >
      <ListItemButton role={undefined} onClick={() => onSelectionChange(!selected)} dense>
        <ListItemIcon>
          <Checkbox
            edge="start"
            checked={selected}
            tabIndex={-1}
            disableRipple
            slotProps={{
              input: { "aria-labelledby": labelId },
            }}
          />
        </ListItemIcon>
        <ListItemText id={labelId} primary={`${cost.bookingNumber} (${cost.order.name})`} />
      </ListItemButton>
    </ListItem>
  );
};

interface CostListProps {
  providerId: string;
  costs: Cost[];
  selected: Record<string, number | null>;
  currency: CostCurrency;
  onSelectedChange: (selected: Record<string, number | null>) => void;
}

const CostList: FC<CostListProps> = ({ providerId, selected, costs, currency, onSelectedChange }) => {
  const sum = Object.values(selected)
    .filter((v) => !isNil(v))
    .reduce((prev, curr) => prev + curr, 0);
  return (
    <>
      <List>
        {costs
          .filter((c) => c.provider.id === providerId)
          .map((c) => (
            <CostListItem
              key={c.id}
              cost={c}
              selected={has(selected, c.id)}
              value={selected[c.id] ?? undefined}
              currency={currency}
              onSelectionChange={(v) =>
                v ? onSelectedChange({ ...selected, [c.id]: null }) : onSelectedChange(omit(selected, c.id))
              }
              onValueChange={(v) => onSelectedChange({ ...selected, [c.id]: v ?? null })}
            />
          ))}
      </List>
      <Stack
        direction="row"
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <Typography variant="body2">Suma: {sum}</Typography>
      </Stack>
    </>
  );
};

interface DialogProps {
  costs: Cost[];
  open: boolean;
  onClose: () => void;
}

const BatchCostInvoiceDialog: FC<DialogProps> = ({ open, costs, onClose }) => {
  const { addInvoiceToCosts } = useCostsInvoice();
  const [providerId, setProviderId] = useState<string | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(dayjs().format(ISO_DATE_FORMAT));
  const [invoiceCurrency, setInvoiceCurrency] = useState<CostCurrency>("EUR");
  const [invoiceStatus, setInvoiceStatus] = useState<CostInvoiceStatus>("FOR_PAYMENT");
  const [selected, setSelected] = useState<Record<string, number | null>>({});
  const [file, setFile] = useState<File | undefined>(undefined);
  const [exchangeRates, setExchangeRates] = useState<SelectedExchangeRates>({});

  const updateExchangeRates = (newSelected: Record<string, number | null>, invoiceCurrency: CostCurrency) => {
    const costCurrencies = uniq(costs.filter((c) => has(newSelected, c.id)).map((c) => c.order.currency));
    setExchangeRates(
      Object.fromEntries(
        (
          [
            ["EUR", exchangeRates.EUR ?? null],
            ["PLN", exchangeRates.PLN ?? null],
          ] as const
        )
          .filter((e) => costCurrencies.includes(e[0]))
          .filter((e) => e[0] !== invoiceCurrency),
      ),
    );
  };

  const handleSelectedChange = (newSelected: Record<string, number | null>) => {
    setSelected(newSelected);
    updateExchangeRates(newSelected, invoiceCurrency);
  };

  const handleInvoiceCurrencyChange = (currency: CostCurrency | null) => {
    if (!currency) {
      return;
    }
    setInvoiceCurrency(currency);
    updateExchangeRates(selected, currency);
  };

  const handleSubmit = async () => {
    const costsToAdd = Object.entries(selected)
      .map(([id, value]) => ({ id, value }))
      .map(({ id, value }) => {
        const cost = costs.find((c) => c.id === id)!;
        if (cost.order.currency === invoiceCurrency) {
          return { id, value: value ?? 0 };
        }
        const exchangeRate = exchangeRates[cost.order.currency as keyof typeof exchangeRates];
        return { id, value: value ?? 0, exchangeRate: exchangeRate ?? undefined };
      });
    await addInvoiceToCosts({
      file: file!,
      invoiceNumber,
      invoiceDate,
      costs: costsToAdd,
      invoiceCurrency,
      invoiceStatus,
    });
    onClose();
  };

  const providers = uniqBy(
    costs.map((c) => c.provider),
    (p) => p.id,
  );
  const addDisabled =
    Object.keys(selected).length === 0 ||
    !invoiceNumber ||
    !file ||
    (Object.keys(exchangeRates).length > 0 && Object.values(exchangeRates).some((v) => v === null));
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Faktura zbiorcza</DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={2}
          sx={{
            marginTop: 1,
          }}
        >
          <Grid size={{ xs: 12, lg: 6 }}>
            <TextField
              label="Numer faktury"
              fullWidth
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <InvoiceStatusSelect value={invoiceStatus} onChange={setInvoiceStatus} />
          </Grid>
          <Grid size={{ xs: 6, lg: 3 }}>
            <DatePicker label="Data faktury" fullWidth value={invoiceDate} onChange={setInvoiceDate} />
          </Grid>
          <Grid size={{ xs: 6, lg: 3 }}>
            <CostCurrencySelect value={invoiceCurrency} onChange={handleInvoiceCurrencyChange} />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <LocalFilePicker label="Faktura" fullWith onFileChange={setFile} />
          </Grid>
          <Grid size={12}>
            <Select
              label="Dostawca"
              fullWidth
              value={providerId ?? ""}
              values={providers.map((p) => ({ value: p.id, label: p.name }))}
              onSelect={(id) => {
                setProviderId(id);
                setSelected({});
              }}
            />
          </Grid>
          <Grid size={12}>
            {providerId && (
              <CostList
                providerId={providerId}
                costs={costs}
                selected={selected}
                currency={invoiceCurrency}
                onSelectedChange={handleSelectedChange}
              />
            )}
          </Grid>
          <ExchangeRates
            sourceCurrency={invoiceCurrency}
            date={invoiceDate}
            exchangeRates={exchangeRates}
            onChange={setExchangeRates}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onClose} />
        <AsyncLoadingButton disabled={addDisabled} onClick={handleSubmit}>
          Dodaj
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

interface Props {
  values: Cost[];
}

const BatchCostInvoice: FC<Props> = ({ values }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
    <>
      <FloatingActionButton color="primary" title="Dodaj fakturę zbiorczą" onClick={() => setDialogOpen(true)}>
        <BatchInvoiceIcon />
      </FloatingActionButton>
      <BatchCostInvoiceDialog
        key={`batch-cost-invoice-dialog-${dialogOpen}`}
        costs={values}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};

export default BatchCostInvoice;
